// Shared axis/time label formatting for reporting charts.

// Recharts picks "nice" tick steps (0.2, 2.5, 1,250...), so an axis label
// keeps exactly the decimals the tick has (up to 2) instead of rounding by
// magnitude, which collapsed 0.2/0.4 into "0" and 12.5 into "13".
const trimmed = (n) => Number(Number(n).toFixed(2));

export function formatAxisNumber(value, format, compact = false) {
  const n = Number(value);
  if (value == null || Number.isNaN(n)) return "";

  if (format === "percentage") return `${trimmed(n * 100)}%`;

  if (compact && Math.abs(n) >= 1000) {
    const compactNum = Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 2,
    }).format(n);
    if (format === "dollars") return `$${compactNum}`;
    if (format === "hours") return `${compactNum}h`;
    return compactNum;
  }

  const t = trimmed(n);
  if (format === "dollars") {
    // Cents are all-or-nothing: $12.50, never $12.5.
    const cents = Number.isInteger(t) ? 0 : 2;
    return `$${t.toLocaleString("en-US", {
      minimumFractionDigits: cents,
      maximumFractionDigits: cents,
    })}`;
  }
  const num = t.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return format === "hours" ? `${num}h` : num;
}

// qh_num is hours into the business day as a float (13.25 = 1:15 PM);
// values past 24 are after midnight. Hour ticks drop ":00" so a full day of
// hourly ticks fits without Recharts thinning them unevenly.
export function formatQhClock(qh, { compactHour = false } = {}) {
  const n = Number(qh);
  if (qh == null || Number.isNaN(n)) return "";
  const totalMinutes = Math.round((((n % 24) + 24) % 24) * 60) % (24 * 60);
  const hour = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const period = hour >= 12 ? "PM" : "AM";
  if (compactHour && minutes === 0) return `${h12}${period}`;
  return `${h12}:${String(minutes).padStart(2, "0")}${period}`;
}

export const formatQhTick = (qh) => formatQhClock(qh, { compactHour: true });

// Whole-hour X ticks for an intraday chart, widening the step (1h, 2h, 3h...)
// so at most `maxTicks` labels show. Left to itself Recharts lands ticks on
// arbitrary quarter-hours and drops overlapping ones unevenly.
export function qhHourTicks(rows, maxTicks = 13) {
  // Category axes match ticks against the raw data values, so return those
  // (qh_num may arrive as a string) rather than recomputed numbers.
  const byHour = new Map();
  for (const row of rows || []) {
    const qh = Number(row?.qh_num);
    if (Number.isInteger(qh) && !byHour.has(qh)) byHour.set(qh, row.qh_num);
  }
  if (byHour.size === 0) return undefined;
  const hours = [...byHour.keys()].sort((a, b) => a - b);
  const first = hours[0];
  const span = hours[hours.length - 1] - first + 1;
  const step = Math.max(1, Math.ceil(span / maxTicks));
  return hours.filter((h) => (h - first) % step === 0).map((h) => byHour.get(h));
}
